/**
 * Home's day strip (D354): the week ahead from what the app already schedules, never a calendar
 * to fill. Seven days from today; a dot for each kind of thing due that day: routines about to
 * run (amber), plans waiting on an approval or due (violet), scheduled releases and pipeline
 * runs (green), pull requests with a review date (rose). Hover names them, and names who owns
 * each when it is somebody else's; pressing a day filters the feed to it; the past lives in the
 * Timeline. Plain data in, plain data out: the desktop and the phone draw the same strip.
 */
import { startOfDay } from './feedModel.js';
export const DOT_FACTS = {
    routine: { tone: 'amber', singular: 'routine runs', plural: 'routines run' },
    plan: { tone: 'violet', singular: 'plan is waiting or due', plural: 'plans are waiting or due' },
    release: { tone: 'green', singular: 'release or pipeline run', plural: 'releases or pipeline runs' },
    review: { tone: 'rose', singular: 'pull request to review', plural: 'pull requests to review' },
};
export const DAYS_AHEAD = 7;
const DOT_ORDER = ['routine', 'plan', 'release', 'review'];
const NAMES_SHOWN = 4;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function dayWords(d) {
    return `${WEEKDAYS_LONG[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
function nth(base, n) {
    return new Date(base.getFullYear(), base.getMonth(), base.getDate() + n);
}
/** What each dot says: "2 routines run: Morning review, Nightly report (Dana's)". */
export function dotWords(dot) {
    const f = DOT_FACTS[dot.kind];
    const head = `${dot.count} ${dot.count === 1 ? f.singular : f.plural}`;
    if (!dot.names.length)
        return head;
    const more = dot.count - dot.names.length;
    const owners = dot.owners.length ? ` (${dot.owners.map((o) => `${o}’s`).join(', ')})` : '';
    return `${head}: ${dot.names.join(', ')}${more > 0 ? ` and ${more} more` : ''}${owners}`;
}
function summaryOf(dots, isToday) {
    if (!dots.length)
        return isToday ? 'Nothing is scheduled for today.' : 'Nothing is scheduled.';
    return dots.map((d) => `${d.count} ${d.count === 1 ? DOT_FACTS[d.kind].singular : DOT_FACTS[d.kind].plural}`).join(', ') + '.';
}
/** Seven days from today, each with the dots its events make. */
export function weekAhead(events, now) {
    const base = new Date(startOfDay(now));
    return Array.from({ length: DAYS_AHEAD }, (_, i) => {
        const d = nth(base, i);
        const start = d.getTime();
        const end = nth(base, i + 1).getTime();
        const mine = events.filter((e) => e.at >= start && e.at < end).sort((a, b) => a.at - b.at);
        const dots = [];
        for (const kind of DOT_ORDER) {
            const of = mine.filter((e) => e.kind === kind);
            if (of.length)
                dots.push({ kind, count: of.length, names: [...new Set(of.map((e) => e.label))].slice(0, NAMES_SHOWN), owners: [...new Set(of.map((e) => e.owner).filter((o) => !!o))] });
        }
        const isToday = i === 0;
        return {
            start,
            weekday: WEEKDAYS[d.getDay()],
            date: d.getDate(),
            isToday,
            dots,
            events: mine,
            summary: summaryOf(dots, isToday),
            label: `${isToday ? 'Today, ' : ''}${dayWords(d)}${dots.length ? `: ${dots.map(dotWords).join('; ')}` : ': nothing scheduled'}`,
        };
    });
}
/** The routines about to run, from now to the end of the week. */
export function routineEvents(routines, now) {
    const out = [];
    for (const r of routines) {
        if (!r.active)
            continue;
        for (const at of r.fires)
            if (at >= now)
                out.push({ kind: 'routine', at, label: r.name, ...(r.owner ? { owner: r.owner } : {}) });
    }
    return out;
}
/** How many routines fire today still to come, for the Routines chip. */
export function routinesToday(cells) {
    return cells[0]?.events.filter((e) => e.kind === 'routine').length ?? 0;
}
export function routinesChipWords(count) {
    return count === 0 ? 'none scheduled today' : `${count} scheduled today`;
}
export function needsYouChipWords(count) {
    return count === 0 ? 'nothing waiting' : `${count} ${count === 1 ? 'item' : 'items'}`;
}
/** The day pressed, kept inside the strip: today when it is not one of the seven (the strip rolled over at midnight). */
export function selectedDay(cells, pressed) {
    return cells.find((c) => c.start === pressed) ?? cells[0];
}
/** The feed's day filter for a pressed day (`buildFeed`): cards that wait on the person ignore it. */
export function dayFilter(cells, pressed) {
    return { day: selectedDay(cells, pressed).start, today: cells[0].start };
}
//# sourceMappingURL=dayStripModel.js.map